import AltRouteIcon from '@mui/icons-material/AltRoute'
import BookmarksIcon from '@mui/icons-material/Bookmarks'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import MonitorHeartTwoToneIcon from '@mui/icons-material/MonitorHeartTwoTone'
import NetworkPingIcon from '@mui/icons-material/NetworkPing'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import ReportIcon from '@mui/icons-material/Report'
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import TroubleshootIcon from '@mui/icons-material/Troubleshoot'
import SwitchAccessShortcutIcon from '@mui/icons-material/SwitchAccessShortcut'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import ArticleIcon from '@mui/icons-material/Article'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import {Box, IconButton, List} from '@mui/material'
import MuiDrawer from '@mui/material/Drawer'
import type {Theme} from '@mui/material/styles'
import {styled} from '@mui/material/styles'
import type {CSSProperties, FC, ReactElement} from 'react'
import {StrictMode, useCallback} from 'react'
import {useLocation} from 'react-router-dom'
import type {UserContextProps} from '@containers/App/UserProvider'
import {useUserContext} from '@containers/App/UserProvider'
import DrawerListItem from '@containers/Layout/drawer/DrawerListItem'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useLogo} from '@hooks/useLogo'
import {usePermittedNavigation} from '@hooks/usePermittedNavigation'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import {getPreferredHomeRoute} from '@shared/utils/homeRoutes'

const drawerWidth: number = 240

const openedMixin = (theme: Theme): CSSProperties => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
    duration: '250ms',
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSProperties => ({
  transition: theme.transitions.create('width', {
    easing: 'cubic-bezier(0.4, 0.0, 1, 1)',
    duration: '200ms',
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {width: `calc(${theme.spacing(8)} + 1px)`},
})

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
  ({theme, open}) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
        duration: '250ms',
      }),
      overflowX: 'hidden',
      '& .MuiDrawer-paper': {
        backgroundColor: theme.palette.background.default,
        ...openedMixin(theme),
      },
    }),
    ...(!open && {
      transition: theme.transitions.create('width', {
        easing: 'cubic-bezier(0.4, 0.0, 1, 1)',
        duration: '200ms',
      }),
      overflowX: 'hidden',
      width: `calc(${theme.spacing(7)} + 1px)`,
      [theme.breakpoints.up('sm')]: {width: `calc(${theme.spacing(8)} + 1px)`},
      '& .MuiDrawer-paper': {
        backgroundColor: theme.palette.background.default,
        ...closedMixin(theme),
      },
    }),
  }),
)

type Props = {
  open: boolean
  onDrawerOpen: () => void
  isMobile?: boolean
}

const MBAIMainDrawer: FC<Props> = ({open, onDrawerOpen, isMobile = false}): ReactElement => {
  const {mainLogo} = useLogo()
  const [t] = useCustomTranslation('common')
  const [t2] = useCustomTranslation('settings')
  const [t3] = useCustomTranslation('program_signal_threshold')
  const [tIncidents] = useCustomTranslation('incidents')
  const [tTasks] = useCustomTranslation('tasks')
  const location = useLocation()
  const {hasExternalBookmarks, currentUser, homeViewTransitionTarget} =
    useUserContext() as UserContextProps
  const {canViewSidebarItem} = usePermittedNavigation()
  const handleDrawerOpen = useCallback(() => {
    onDrawerOpen()
  }, [onDrawerOpen])
  const prefersLegacyHome = currentUser?.userprofile?.home_view_mode === 'legacy'
  const isOnLegacyHomeRoute = location.pathname.startsWith('/lines_overview')
  const isSwitchingToNewHome = homeViewTransitionTarget === 'new' && isOnLegacyHomeRoute
  const effectiveLegacyHome = prefersLegacyHome || isSwitchingToNewHome
  const showLinesOverviewItem = !prefersLegacyHome && !isSwitchingToNewHome
  const preferredHomeLink = getPreferredHomeRoute(currentUser?.userprofile?.home_view_mode)

  const listItemsTop = [
    {
      title: t('home'),
      icon: <HomeOutlinedIcon />,
      link: preferredHomeLink,
      id: 'mbai-main-home-link',
      extraActivePaths: effectiveLegacyHome ? ['/lines_overview'] : ['/home'],
    },
    ...(showLinesOverviewItem
      ? [
          {
            title: t('sidebar.lines_overview'),
            icon: <MonitorHeartTwoToneIcon />,
            link: '/lines_overview?view=grid',
            id: 'mbai-main-line-overview-link',
          },
        ]
      : []),
    {
      title: t('sidebar.cockpit'),
      icon: <LocationOnIcon />,
      link: '/cockpit',
      id: 'mbai-main-cockpit-link',
    },
    {
      title: t('sidebar.custom_dashboards'),
      icon: <DashboardCustomizeIcon />,
      link: '/dashboards',
      id: 'mbai-main-custom-dashboards-link',
    },
    {
      title: t('sidebar.batch_traceability'),
      icon: <AltRouteIcon sx={{transform: 'rotate(90deg)'}} />,
      link: '/batch_traceability',
      id: 'mbai-main-batch-traceability-link',
    },
    {
      title: t('signals_page.page_title'),
      icon: <QueryStatsIcon />,
      link: '/signal-explorer',
      id: 'mbai-main-signals-link',
    },
    {
      title: t('anomalies'),
      icon: <TroubleshootIcon />,
      link: '/anomalies',
      id: 'mbai-main-anomalies-link',
    },
    {
      title: t('logbook'),
      icon: <TextSnippetIcon />,
      link: '/logbook',
      id: 'mbai-main-logbook-link',
    },
    {
      title: t3('program_signal_threshold_violations'),
      icon: <NetworkPingIcon />,
      link: '/threshold-violations',
      id: 'mbai-main-thresholds-link',
    },
    {
      title: t('signal_value_tracker.title'),
      icon: <SwitchAccessShortcutIcon />,
      link: '/signal-value-trackers',
      id: 'mbai-main-signal-value-trackers-link',
    },
    {
      title: tIncidents('labels.incidents'),
      icon: <ReportIcon />,
      link: '/incidents',
      id: 'mbai-main-incidents-link',
    },
    // Above Tasks, and in the main nav rather than under Settings → Machine
    // Admin. Reports are a thing people read, not a thing they configure: the
    // templates are configuration, but the reports the plant produces are
    // day-to-day output, and burying output three levels into a settings tree
    // is the reason nobody found it.
    {
      title: t('reporting.title'),
      icon: <ArticleIcon />,
      link: '/reports',
      id: 'mbai-main-reports-link',
    },
    {
      title: tTasks('page_title'),
      icon: <TaskAltIcon />,
      link: '/tasks',
      id: 'mbai-main-tasks-link',
    },
  ]

  const listItemsBottom = [
    {
      title: t2('title'),
      icon: <SettingsTwoToneIcon />,
      link: '/settings',
      id: 'mbai-main-settings-link',
    },
  ]

  if (hasExternalBookmarks) {
    listItemsBottom.unshift({
      title: t('sidebar.external_bookmarks'),
      icon: <BookmarksIcon />,
      link: '/external_bookmarks',
      id: 'mbai-main-external-bookmarks-link',
    })
  }

  const drawerContent = (
    <>
      <Box
        sx={{
          height: 58,
          pl: 2,
          pr: 1.4,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          justifyContent: 'flex-start',
        }}
      >
        <IconButton
          size="small"
          onClick={handleDrawerOpen}
          aria-label={open ? t('navigation.collapse_drawer') : t('navigation.expand_drawer')}
          sx={{
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
          }}
        >
          <img
            src={mainLogo}
            alt="MontBlancAI MenuToggleLogo"
            style={{
              display: 'block',
              width: '24px',
              height: '24px',
            }}
          />
        </IconButton>
      </Box>
      <List>
        {/* A page the user holds no read permission for is not listed: the API
            would return nothing there anyway, so the entry was only ever a dead
            end. See navigationPermissions.ts for the per-entry mapping. */}
        {listItemsTop
          .filter((text) => canViewSidebarItem(text.id))
          .map((text, index) => (
            <DrawerListItem key={text.id} text={text} index={index} open={open} idProp={text.id} />
          ))}
      </List>
      <List sx={{marginTop: 'auto'}}>
        {listItemsBottom.map((text, index) => (
          <DrawerListItem key={text.id} text={text} index={index} open={open} idProp={text.id} />
        ))}
      </List>
    </>
  )

  return (
    <StrictMode>
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={open}
          onClose={handleDrawerOpen}
          ModalProps={{keepMounted: true}}
          sx={{
            '& .MuiPaper-root': {
              width: drawerWidth,
              backgroundColor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
              boxSizing: 'border-box',
            },
            '& .MuiSvgIcon-root': {fontSize: '1.1rem'},
          }}
          data-testid="mbai-layout-drawer-component"
          id="mbai-main-layout-drawer-component"
        >
          {drawerContent}
        </MuiDrawer>
      ) : (
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
            },
            '& .MuiSvgIcon-root': {fontSize: '1.1rem'},
            display: {
              xs: 'none',
              sm: 'block',
            },
          }}
          data-testid="mbai-layout-drawer-component"
          id="mbai-main-layout-drawer-component"
        >
          {drawerContent}
        </Drawer>
      )}
    </StrictMode>
  )
}

export default MBAIMainDrawer
